import React from "react";
import { useNavigate } from "react-router-dom";

export default function PlexysAppPrivacy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <button onClick={() => navigate("/")} className="flex items-center">
            <h1 className="text-2xl font-bold" style={{ color: '#30853A' }}>Plexys</h1>
          </button>
          <button
            onClick={() => navigate("/")}
            className="px-4 py-2 text-gray-600 hover:text-gray-900"
          >
            ← Back to Home
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center gap-3 mb-2">
          <span className="bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full">
            iOS App
          </span>
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Plexys Privacy Policy</h1>
        <p className="text-lg text-gray-500 mb-2">Spine Surgery Companion — iOS App</p>
        <p className="text-sm text-gray-500 mb-8">
          Effective Date: February 3, 2026 · Last Updated: February 6, 2026
        </p>

        <div className="prose prose-lg max-w-none space-y-6">
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Introduction</h2>
            <p className="text-gray-700">
              Plexys ("we," "our," or "the app") is a health companion app designed to help spine surgery patients 
              prepare for medical consultations, track symptoms, monitor mobility, and navigate post-operative recovery. 
              This Privacy Policy explains how we collect, use, and protect your information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">Information You Provide</h3>
            <p className="text-gray-700 mb-3">When you use Plexys, you may provide:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Personal Information:</strong> Name, date of birth, contact information</li>
              <li><strong>Health Information:</strong> Symptom descriptions, pain locations and levels, medical history, surgical history, current medications, and treatment goals</li>
              <li><strong>Symptom Journal Entries:</strong> Daily pain levels, triggers, mood, sleep quality, and notes</li>
              <li><strong>Clinical Questionnaire Responses:</strong> Neck Disability Index (NDI), Oswestry Disability Index (ODI), and Modified Japanese Orthopaedic Association (MJOA) scores</li>
              <li><strong>Recovery Plans:</strong> Procedure type, surgery date, and milestone progress</li>
              <li><strong>Imaging Records:</strong> Photos of imaging studies you choose to capture</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">Apple HealthKit Data</h3>
            <p className="text-gray-700 mb-3">With your explicit permission, Plexys accesses the following data from Apple HealthKit:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Step count</li>
              <li>Walking + running distance</li>
              <li>Flights climbed</li>
              <li>Walking speed</li>
              <li>Walking step length</li>
              <li>Walking asymmetry percentage</li>
              <li>Walking double support percentage</li>
              <li>Walking steadiness (Apple Walking Steadiness)</li>
              <li>Stair ascent and descent speed</li>
            </ul>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
              <h4 className="font-semibold text-green-900 mb-2">Why We Access This Data</h4>
              <p className="text-green-800 text-sm">
                Mobility data helps you and your healthcare provider understand your functional status and track 
                changes over time. The <strong>Gait Tracker</strong> feature uses this data to establish a personal 
                baseline and alert you if your mobility metrics change significantly — which can be an early indicator 
                of neurological changes such as myelopathy progression. This information is also included in your 
                visit preparation summary to give your surgeon objective data about your daily mobility patterns.
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <h4 className="font-semibold text-blue-900 mb-2">HealthKit Data Handling</h4>
              <ul className="list-disc list-inside text-blue-800 text-sm space-y-1">
                <li>HealthKit data is read directly from your device</li>
                <li>We do NOT upload HealthKit data to any external servers</li>
                <li>HealthKit data is only used within the app for mobility tracking, gait analysis, and visit summaries</li>
                <li>You can revoke HealthKit access at any time in iOS Settings</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">How We Use Your Information</h2>
            <p className="text-gray-700 mb-3">We use your information to:</p>
            <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Generate Visit Summaries:</strong> Create PDF documents summarizing your symptoms, history, and mobility data for your healthcare provider</li>
              <li><strong>Track Your Health:</strong> Display trends in your mobility, gait metrics, and symptom scores over time</li>
              <li><strong>Gait Monitoring:</strong> Compare your current mobility metrics against your personal baseline and alert you to significant changes</li>
              <li><strong>Recovery Tracking:</strong> Guide your post-operative recovery with milestone tracking and activity recommendations</li>
              <li><strong>Symptom Journaling:</strong> Track daily symptoms, triggers, and relief methods to identify patterns</li>
              <li><strong>Improve Your Experience:</strong> Remember your preferences and previously entered information</li>
            </ol>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Storage</h2>
            
            <div className="bg-gray-100 border border-gray-300 rounded-lg p-6 mb-4">
              <h3 className="text-xl font-bold text-gray-900 mb-3">🔒 Local Storage Only</h3>
              <p className="text-gray-700 mb-3"><strong>All your data is stored locally on your device.</strong> Plexys does not:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Upload your data to cloud servers</li>
                <li>Share your data with third parties</li>
                <li>Require an internet connection to function</li>
                <li>Create user accounts or online profiles</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Your Control</h3>
            <p className="text-gray-700 mb-3">You have complete control over your data:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Export:</strong> Generate PDF summaries to share with your healthcare provider</li>
              <li><strong>Delete:</strong> Remove all app data by deleting the app from your device</li>
              <li><strong>HealthKit:</strong> Manage permissions in iOS Settings → Privacy & Security → Health → Plexys</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Sharing</h2>
            <p className="text-gray-700 mb-3">
              We do not sell, rent, or share your personal information with third parties.
            </p>
            <p className="text-gray-700">
              The only way your health information leaves your device is when you explicitly choose to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-2">
              <li>Share a PDF summary via email, messaging, or other apps</li>
              <li>Show your screen to your healthcare provider</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Security</h2>
            <p className="text-gray-700 mb-3">We implement appropriate technical measures to protect your information:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>All data remains on your device</li>
              <li>No network transmission of health data</li>
              <li>Standard iOS data protection and encryption</li>
              <li>Camera and photo library access restricted to imaging capture only</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Children's Privacy</h2>
            <p className="text-gray-700">
              Plexys is not intended for use by children under 18 without parental supervision. We do not 
              knowingly collect information from children under 13.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to This Policy</h2>
            <p className="text-gray-700">
              We may update this Privacy Policy from time to time. We will notify you of any changes by updating the 
              "Last Updated" date at the top of this policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Rights</h2>
            <p className="text-gray-700 mb-3">
              Depending on your location, you may have rights regarding your personal information, including:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Access to your data</li>
              <li>Correction of inaccurate data</li>
              <li>Deletion of your data</li>
              <li>Data portability</li>
            </ul>
            <p className="text-gray-700 mt-3">
              Since all data is stored locally on your device, you can exercise these rights directly through the 
              app or by managing your device.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Apple HealthKit Disclosure</h2>
            <p className="text-gray-700 mb-3">
              Plexys integrates with Apple HealthKit to read mobility and activity data. In accordance with Apple's requirements:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>We do not use HealthKit data for advertising or marketing purposes</li>
              <li>We do not sell HealthKit data to third parties</li>
              <li>We do not share HealthKit data with third parties for their marketing purposes</li>
              <li>HealthKit data is not stored outside of your device</li>
              <li>We only access the specific HealthKit data types listed in this policy</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-gray-700 mb-3">
              If you have questions about this Privacy Policy or our privacy practices, please contact us at:
            </p>
            <p className="text-gray-700">
              <strong>Email:</strong> privacy@plexys.co<br />
              <strong>Website:</strong>{" "}
              <a href="https://www.plexys.co" className="text-green-700 hover:underline">
                https://www.plexys.co
              </a>
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-200 text-center text-sm text-gray-500">
          <p>This privacy policy is provided in compliance with applicable privacy laws and Apple App Store requirements.</p>
          <p className="mt-2">© {new Date().getFullYear()} Plexys. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
